import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { NamedIcon } from '@/lib/icons';
import { cn } from '@/lib/utils';

export interface Choice<T extends string> {
  value: T;
  label: string;
  icon?: string;
}

const chip =
  'inline-flex h-11 items-center gap-2 rounded-full border px-4 text-[15px] font-medium transition-colors aria-checked:border-foreground aria-checked:bg-foreground aria-checked:text-background aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-background';

/** Pick one: a row of chips that behaves as a radio group. */
export function SingleChips<T extends string>({ label, options, value, onChange }: { label: string; options: Choice<T>[]; value: T; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <motion.button key={o.value} type="button" role="radio" aria-checked={value === o.value} whileTap={{ scale: 0.95 }} onClick={() => onChange(o.value)} className={chip}>
          {o.icon && <NamedIcon name={o.icon} className="size-[18px]" />}
          {o.label}
        </motion.button>
      ))}
    </div>
  );
}

/** Pick any: chips that toggle, with a check on the chosen ones. */
export function MultiChips<T extends string>({ label, options, value, onChange }: { label: string; options: Choice<T>[]; value: T[]; onChange: (v: T[]) => void }) {
  const toggle = (v: T) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <motion.button key={o.value} type="button" aria-pressed={on} whileTap={{ scale: 0.95 }} onClick={() => toggle(o.value)} className={cn(chip)}>
            {on ? <Check className="size-[18px]" /> : o.icon && <NamedIcon name={o.icon} className="size-[18px]" />}
            {o.label}
          </motion.button>
        );
      })}
    </div>
  );
}
